import { html } from "lit";
import { CustomVessel, HeavyWeatherLimitElement, TEU } from "../heavy-weather-limits-model";

export const  _convertElementToKt = (
    element: HeavyWeatherLimitElement
  ): HeavyWeatherLimitElement =>{
    if (element.unit === "m/s") {
      element.max = parseFloat((element.max! * 1.94384).toFixed(1));
      element.unit = "kt";
    }
    return element;
  }

  export const _convertElementToMs =(
    element: HeavyWeatherLimitElement
  ): HeavyWeatherLimitElement=> {
    if (element.unit === "kt") {
      element.max = parseFloat((element.max! / 1.94384).toFixed(1));
      element.unit = "m/s";
    }
    return element;
  }

  export const _isTEU = (obj: any): obj is TEU=> {
      return (
        obj &&
        typeof obj === "object" &&
        "teuLessthan2500" in obj &&
        "teuBetween2500to5000" in obj &&
        "teuMorethan5000" in obj &&
        (typeof obj.teuLessthan2500 === "number" ||
          obj.teuLessthan2500 === null) &&
        (typeof obj.teuBetween2500to5000 === "number" ||
          obj.teuBetween2500to5000 === null) &&
        (typeof obj.teuMorethan5000 === "number" || obj.teuMorethan5000 === null)
      );
    }
  
export const extractChangeData = (oldList: CustomVessel[], newList: any[]) => {
  const changeHistory: { [imo: string]: any[] } = {};

  for (const newItem of newList) {
    const { imo, customLimits: newLimits } = newItem;
    const oldItem = oldList.find((item) => item.imo === imo);
    if (!oldItem) continue;

    const oldLimits = oldItem.customLimits;
    const changes = [];

    for (const key in newLimits) {
      const oldVal = (oldLimits as Record<string, any>)[key];
      const newVal = (newLimits as Record<string, any>)[key];

      // Handle nested objects
      if (
        typeof oldVal === 'object' &&
        oldVal !== null &&
        typeof newVal === 'object' &&
        newVal !== null
      ) {
        for (const nestedKey in newVal) {
          if (oldVal[nestedKey] !== newVal[nestedKey]) {
            changes.push({
              name: `${key}.${nestedKey}`,
              from: oldVal[nestedKey],
              to: newVal[nestedKey],
            });
          }
        }
      } else {
        if (oldVal !== newVal) {
          changes.push({
            name: key,
            from: oldVal,
            to: newVal,
          });
        }
      }
    }

    if (changes.length > 0) {
      changeHistory[imo] = changes;
    }
  }

  return changeHistory;
}

export const  _shouldConvertUnit = (elementName: string) => {
    const convertibleElements = [
      "Wind10M",
      "Wind50M",
      "WindGusts10M",
      "WindGusts50M",
      "RiskWindSpeed",
    ];
    return convertibleElements.includes(elementName);
  }

export const _renderEmptyPage = (message: string): ReturnType<typeof html> => {
  return html`
    <div class="empty-page">
      <div class="empty-page__message">
        ${message}
      </div>
    </div>
    <style>
      .empty-page {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        font-size: 1.2em;
        color: var(--voyage-text-color, #333);
      }
      .empty-page__message {
        text-align: center;
        padding: 20px;
        background-color: var(--voyage-background-color, #f9f9f9);
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      .empty-page__message::before {
        content: "⚠️ ";
        font-size: 1.5em;
        vertical-align: middle;
      }
      .empty-page__message::after {
        content: "You can also use the 'Customise Weather Limits' option from the Vessel Overview page to enroll a vessel.";
        display: block;
        margin-top: 10px;
        font-size: 0.9em;
        color: var(--voyage-secondary-text-color, #666);
      }
      .empty-page__message:hover {
        color: var(--voyage-hover-text-color, #000);
        cursor: pointer;
      }
      .empty-page__message:hover::before {
        color: var(--voyage-hover-icon-color, #ff0000);
      }
      .empty-page__message:hover::after {
        color: var(--voyage-hover-secondary-text-color, #444);
      }
    </style>
  `;
}

export const _emptyCompanyLimit = {
            Wind10M: null,
            Wind50M: null,
            Visibility: null,
            Icing: null,
            Hurricanes: null,
            TropicalStorms: null,
            Depressions: null,
            WindGusts10M: null,
            WindGusts50M: null,
            RiskWindSpeed: null,
            RiskWaveHeight: null,
            HeadWind: null,
            BeamWind: null,
            FollowingWind: null,
            BeamWavesHeight: null,
            BeamWavesPeriod: { max: null, min: null },
            HeadWavesHeight: null,
            HeadWavesPeriod: { max: null, min: null },
            FollowingWavesHeight: null,
            FollowingWavesPeriod: { max: null, min: null } 
}

    const getDefault = (field: string) => {
      if(!field) return "";
      if(field?.includes(".max") || field?.includes(".min")) {
        const [rawName, suffix] = field.split('.');
        const readableName = rawName.replace(/([a-z])([A-Z])/g, '$1 $2');
        return `${readableName} (${suffix})`;
      }
      return field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');    }


export const _getFieldName = (field: string): string => {
    switch(field) {
        case "Wind10M":
            return "Maximum wind speed 10m";
        case "Wind50M":
            return "Maximum wind speed 50m";
        case "WindGusts10M":
            return "Maximum wind gusts 10m";
        case "WindGusts50M":
            return "Maximum wind gusts 50m";
        case "RiskWindSpeed":
            return "Maximum risk wind speed";     
        case "Waves":
            return "Maximum wave height";
        case "RiskWaveHeight":
            return "Maximum risk wave height";
        case "HeadWavesHeight":
            return "Head waves, higher than";
        case "HeadWavesPeriod":
            return "Head waves, higher than (m) periods";
        case "BeamWavesHeight":
            return "Beam waves, higher than";
        case "BeamWavesPeriod":
            return "Beam waves, higher than (m) periods";
        case "FollowingWavesHeight":
            return "Following waves, higher than";
        case "FollowingWavesPeriod":
            return "Following waves, higher than (m) periods";
        case "Visibility":
            return "Minimum visibility";
        case "Icing":
            return "Maximum risk of icing";
        case "Hurricanes":
            return "Hurricanes";
        
        default:
            return getDefault(field) || ""
    }

}

export const convertMsToKt = (ms: number): number => {
  return ms * 1.94384;
}
